'use client';

import { faLoader } from '@fortawesome/pro-light-svg-icons';
import { faQrcode, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalContent } from '@pickleballinc/react-ui';
import jsPDF from 'jspdf';
import { useEffect, useState } from 'react';

import { useGetLeagueQRCodeQueryData } from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/queries';
import { LeagueDetails, SessionLeagueDetails } from '@/modules/league/new-components/types';

export interface ExportQRCodeModalProps {
  isAuthorized: boolean;
  details: LeagueDetails;
  session: SessionLeagueDetails;
  setShowQrCode: (showQRCode: boolean) => void;
  showQrCode: boolean;
}

export default function ExportQRCodeModal({
  isAuthorized,
  details,
  session,
  showQrCode,
  setShowQrCode,
}: ExportQRCodeModalProps) {
  const { LeagueID: leagueId, ClubID: clubId, Title: leagueTitle, ClubTitle: clubTitle } = details;
  const { LeagueSessionID: sessionId, SessionNumber: sessionNumber } = session;

  const { data, isLoading } = useGetLeagueQRCodeQueryData(leagueId, sessionId, clubId);

  const [qrData, setQRData] = useState('');

  useEffect(() => {
    if (isAuthorized && data) {
      setQRData(data);
    }
  }, [isAuthorized, data]);

  const exportQRCodeAsPDF = (
    title: string | undefined,
    clubTitle: string | undefined,
    sessionNumber: number | undefined,
    qrDataURL: string
  ) => {
    if (!qrDataURL) return;

    const generatePDF = async () => {
      const doc = new jsPDF();
      const qrCodeImage = await fetch(qrDataURL).then((res) => res.blob());
      const centerX = doc.internal.pageSize.getWidth() / 2;
      const qrCodeSize = 50;
      const qrCodeX = centerX - qrCodeSize / 2;
      const qrCodeY = title || clubTitle ? 70 : 30;

      if (title) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.setTextColor('#333');
        doc.text(`League: ${title}`, centerX, qrCodeY - 30, { align: 'center' });
      }

      if (sessionNumber) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor('#666');
        doc.text(`League Session: ${sessionNumber}`, centerX, qrCodeY - 20, { align: 'center' });
      }

      if (clubTitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(18);
        doc.setTextColor('#666');
        doc.text(`Club: ${clubTitle}`, centerX, qrCodeY - 10, { align: 'center' });
      }

      const qrCodeUrlObject = URL.createObjectURL(qrCodeImage);
      doc.setDrawColor('#333');
      doc.setLineWidth(2);
      doc.setFillColor('#f4f4f4');
      doc.roundedRect(qrCodeX - 5, qrCodeY - 5, qrCodeSize + 10, qrCodeSize + 10, 5, 5, 'FD');
      doc.addImage(qrDataURL, 'PNG', qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);
      doc.setFontSize(12);
      doc.setTextColor('#666');
      doc.text(`Scan QR code to join ${title}`, centerX, qrCodeY + qrCodeSize + 20, {
        align: 'center',
      });
      doc.save(
        `${title || clubTitle ? (title ? title : '') + ' ' + (clubTitle ? clubTitle : '') : 'League'} QR_Code.pdf`
      );
      URL.revokeObjectURL(qrCodeUrlObject);
    };

    generatePDF();
  };

  const handleCloseModal = () => {
    setShowQrCode(false);
  };

  const handleExportQRCodeAsPDF = () => {
    exportQRCodeAsPDF(leagueTitle, clubTitle, sessionNumber, qrData);
  };

  return (
    <Modal open={showQrCode} onOpenChange={handleCloseModal}>
      <ModalContent className='flex h-[450px] w-[calc(100%-16px)] flex-col gap-4 !p-6 !outline-none md:h-auto md:max-w-[500px]'>
        <div className='sticky top-0 flex flex-col gap-4 bg-white'>
          <div className='relative'>
            <div className='mx-auto mb-4 flex size-16 items-center justify-center md:size-24'>
              <FontAwesomeIcon icon={faQrcode} className='text-black' size='4x' />
            </div>
            <h3 className='pr-4 text-center text-lg font-semibold leading-[22px] text-gray-900'>
              Join League by QR code
            </h3>
            <div
              className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
              onClick={handleCloseModal}
            >
              <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
            </div>
          </div>
        </div>
        <div className='py-4 text-left text-sm md:text-base gap-2 flex flex-col text-gray-900'>
          <p>This button is only shown to the managers of the league and/or club.</p>
          <p>
            Click <b>&quot;OK&quot;</b> below to get a printout of a QR code that players can scan
            and automatically be placed into the league.
          </p>
          <p>
            If the league requires membership AND the club has membership turned on, the player will
            also automatically be placed in the club as a member.
          </p>
        </div>
        <div className='flex items-center justify-between gap-4'>
          <Button
            variant='secondary'
            className='flex w-[200px] justify-center border !text-sm !text-gray-500 focus:!shadow-[unset]'
            onClick={handleCloseModal}
          >
            Close
          </Button>

          <Button
            variant='primary'
            className='flex w-[200px] justify-center !text-sm focus:!shadow-[unset]'
            disabled={!qrData || isLoading}
            onClick={handleExportQRCodeAsPDF}
            prefixIcon={
              isLoading ? <FontAwesomeIcon icon={faLoader} className='animate-spin' /> : null
            }
          >
            {isLoading ? 'Loading..' : 'Ok'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
